import { useEffect, useMemo, useRef, useState } from "react"
import CardFace from "@/components/CardFace/CardFace"
import { CARDS_COPY } from "@/content/cardsRound"
import { parseCardId } from "@/content/cards"
import { cardTint } from "@/components/CardFace/cardTint"
import type { CardId } from "@/types"
import styles from "@/styles/pages/Cards.module.css"

/*
 * One card at a time: skip it or keep it.
 *
 * This is the round's default because it is the one that produces surprise. A
 * spread lets a room pick the four capabilities it already had in mind; a deal
 * makes it look at all thirty-two and say no to each one out loud, and the cards
 * someone hesitates over are the ones worth asking about.
 *
 * Throwing works by pointer events rather than HTML5 drag, because drag has no
 * touch story and this is the one interaction guests do on phones. The card
 * follows the finger, and past a threshold the release decides — short of it,
 * the card springs back and nothing is written.
 */

/** A plain no, not a direction — the throw itself still reads left/right. */
function SkipIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 3l10 10M13 3L3 13"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Wants it — the filled circle underneath is what does the rest of the
 *  telling. */
function KeepIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 13.6 2.6 8.4a3.4 3.4 0 0 1 4.9-4.7L8 4.2l.5-.5a3.4 3.4 0 0 1 4.9 4.7L8 13.6Z" />
    </svg>
  )
}

/*
 * How far a throw has to travel before release counts as a decision.
 *
 * 120px, matching the prototype. Lower than that and reading the card by
 * dragging it out from under your thumb spends a slot in the hand; this is the
 * one gesture guests make on a phone, so an accidental keep is expensive.
 */
const THROW_THRESHOLD_PX = 120
/*
 * Below this, a release is a tap rather than an abandoned drag — it turns the
 * card over instead of springing it back. A few pixels of finger wobble must
 * still count as a tap; a real drag that fell short of the throw threshold
 * must not also flip the card underneath the gesture that failed.
 */
const TAP_THRESHOLD_PX = 4
/* Past the threshold, how far a passed card flies before the next one appears. */
const FLIGHT_PX = 620
/*
 * A keep is not a throw. A passed card is flung off the table; a kept one is set
 * DOWN on it — it straightens out of the drag, lowers toward the hand below and
 * shrinks as it meets the surface, and the next card is revealed underneath
 * rather than in the empty space something just left through. Two different
 * gestures for two different decisions: sending a card you want off the right
 * edge said "gone" in the same breath as "kept", and on a phone the hand it was
 * going into is the row directly under the thumb that threw it.
 *
 * Short travel on purpose. It is the settling that reads as placement, not the
 * distance; a card that crossed the gap to the hand slots would have to be
 * measured against a row whose height changes with the cap, and would arrive
 * before the pile behind it had risen.
 */
const PLACE_DOWN_PX = 46
const PLACE_SCALE = 0.9
/* Kept in step with the flight and placement transitions in Cards.module.css. */
const FLIGHT_MS = 340

/*
 * The cards peeking from underneath, nearest first. Two of them, at deliberately
 * uneven angles: a tidy stack reads as a UI element, a scruffy one reads as a
 * deck someone has been handling. Two rather than a fan, because the pile has to
 * say "there is more to come" without competing with the card being read — a
 * third layer only adds edge, and the arrival below is what sells the depth.
 *
 * They are the actual next cards, drawn by `CardFace` like every other card in
 * the app, and `dim` is how far each is shaded back — see `Peek`.
 */
const PEEK = [
  { rotate: 2.4, x: 8, y: 10, scale: 0.982, dim: "10%" },
  { rotate: -3.6, x: -12, y: 19, scale: 0.958, dim: "20%" },
]
const DEEPEST = PEEK[PEEK.length - 1]
/*
 * Where the card being read sits: flat, unshaded, dead centre. It is written as
 * a peek shape because that is what the nearest peek rises to while a card is
 * being thrown — see `shapeAt`.
 */
const TOP = { rotate: 0, x: 0, y: 0, scale: 1, dim: "0%" }

/*
 * The arrival: the deck is dealt onto the table rather than being there already.
 *
 * Cards fly in from off the right edge, one after another and deepest first, so
 * what lands is visibly a deck with every family in it and the last card down is
 * the one being read.
 *
 * Two cards per family, not the whole deck.
 *
 * This has been cut both ways and both were wrong. Four cards off the FRONT of
 * the deck read as a short pile of one colour, because the deck is grouped by
 * family. All thirty-two dropped frames: each card is a CardFace, which is four
 * stacked background layers, and they are all in flight at once. What the sweep
 * has to show is the colour range, and a family's second card says nothing about
 * that its first did not — so the range is what gets sampled, at half the cost.
 *
 * Everything behind the two peeks lands on exactly the deepest peek's resting
 * transform, so the pile left behind IS the two-card stack the round settles on
 * and dropping them when the intro ends moves nothing on screen. Anything more
 * honest — a dozen cards fanning out and then collapsing — would be a jump at
 * the end of a one-second animation.
 *
 * The span is fixed and the stagger is divided out of it, rather than the other
 * way round: a per-card delay would make the wait before the top card lands a
 * function of how many families are switched on, and the room cannot start the
 * round until it does. Six families deal fast, one deals slowly, both take the
 * same second.
 */
const DEAL_IN_PER_FAMILY = 2
const DEAL_IN_SPAN_MS = 620
/* Kept in step with the dealIn animation in Cards.module.css. */
const DEAL_IN_MS = 460
const DEAL_IN_TOTAL_MS = DEAL_IN_SPAN_MS + DEAL_IN_MS

/*
 * A card in the pile under the one being read — and, during the arrival, one of
 * the cards flying in.
 *
 * It is a real `CardFace`, not a coloured rectangle: the deck landing has to be
 * THIS deck landing, and a stack of blank slabs reads as a loading state. At
 * rest almost none of it shows — the offsets leave a sliver of edge — so the
 * shade on top is what keeps the top card the only one being read. A wash rather
 * than opacity, because a card behind should look shaded by the one on top of
 * it, not translucent.
 *
 * `onFlip` is deliberately absent: only the card in hand can be turned over, and
 * a flip button under the pile would be a target nobody can hit.
 *
 * Two elements, and they are not interchangeable. `.enter` is the card coming up
 * off the deck and runs once, when this card first joins the pile; `.peek` is
 * where it sits, and TRANSITIONS as the pile rises under a throw. Both are
 * transforms, so they cannot share an element — an animation on `transform`
 * takes that property over, and the rise would stop moving. Keyed by card id by
 * every caller, which is what makes the entrance run for a card arriving at the
 * back and not for the ones already in the pile shuffling forward.
 */
function Peek({ cardId, shape }: { cardId: CardId; shape: (typeof PEEK)[number] }) {
  return (
    <span className={styles.enter}>
      <span
        className={styles.peek}
        style={
          {
            transform: `translate(${shape.x}px, ${shape.y}px) rotate(${shape.rotate}deg) scale(${shape.scale})`,
            "--peek-dim": shape.dim,
          } as React.CSSProperties
        }
      >
        <CardFace cardId={cardId} size="lg" />
        <span className={styles.peekShade} />
      </span>
    </span>
  )
}

/*
 * The first DEAL_IN_PER_FAMILY cards of each family, in the order they are dealt.
 *
 * Taken off the cards BEHIND the pile rather than off the whole deck, so nothing
 * that is already flying in as the top card or a peek flies in twice.
 */
function sampleFamilies(cards: readonly CardId[]): CardId[] {
  const taken = new Map<number, number>()
  return cards.filter((id) => {
    const family = parseCardId(id)?.family ?? 0
    const already = taken.get(family) ?? 0
    if (already >= DEAL_IN_PER_FAMILY) return false
    taken.set(family, already + 1)
    return true
  })
}

interface SwipeDealProps {
  deck: readonly CardId[]
  picked: readonly CardId[]
  /** How many the hand may hold — the footer's own count, not the family position. */
  cap: number
  capReached: boolean
  onKeep: (cardId: CardId) => void
  onDrop: (cardId: CardId) => void
  onSeeSpread: () => void
  /** The hand is settled: the deck still reads, but nothing can be thrown. */
  readOnly: boolean
}

export default function SwipeDeal({
  deck,
  picked,
  cap,
  capReached,
  onKeep,
  onDrop,
  onSeeSpread,
  readOnly,
}: SwipeDealProps) {
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  /*
   * True only while the deck is landing. Under `prefers-reduced-motion` it never
   * is: the stack is simply already on the table, which is the same picture
   * without the flight.
   */
  const [intro, setIntro] = useState(
    () => !(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false)
  )
  const [drag, setDrag] = useState(0)
  /* Non-null while a card is flying out, so the release animates. */
  const [flying, setFlying] = useState<"left" | "right" | null>(null)
  const origin = useRef<number | null>(null)
  /*
   * How far the card has been dragged, readable synchronously.
   *
   * `drag` is the same number, but only as current as the last commit — and a
   * flick can deliver its final pointermove and the pointerup inside one task,
   * so the release reads a distance from before the throw finished and springs
   * a card back that was thrown well past the threshold. The state is for
   * rendering; this is what the decision is made on.
   */
  const travel = useRef(0)
  /*
   * The furthest the card got from the press, which is not `|travel|` — that is
   * net displacement, so a drag out and back reads as nothing having happened
   * and the failed gesture would flip the card on release.
   */
  const excursion = useRef(0)
  /*
   * Taken the instant a decision is made, released when the next card is up.
   * `flying` cannot do this job: two presses in the same tick both read the old
   * value, so both decide, and the second one's `advance` skips a card nobody
   * was ever shown.
   */
  const deciding = useRef(false)
  const flight = useRef<number | null>(null)

  const done = index >= deck.length

  /*
   * Where each family starts in `deck`, in deal order. `deck` is already
   * grouped by family (see `cardsInPlay`), so a family's span is everything
   * between one boundary and the next — no separate lookup into the family
   * table is needed to jump between them.
   */
  const familyStarts = useMemo(() => {
    const starts: number[] = []
    let last: number | null = null
    deck.forEach((id, position) => {
      const family = parseCardId(id)?.family ?? null
      if (family !== last) {
        starts.push(position)
        last = family
      }
    })
    return starts
  }, [deck])

  const familyOrdinal = familyStarts.reduce(
    (ordinal, start, position) => (start <= index ? position : ordinal),
    0
  )

  const goToFamily = (direction: 1 | -1) => {
    if (deciding.current) return
    const target = familyOrdinal + direction
    if (target < 0 || target >= familyStarts.length) return
    setIntro(false)
    setFlipped(false)
    setFlying(null)
    settle()
    setIndex(familyStarts[target])
  }

  const settle = () => {
    setDrag(0)
    travel.current = 0
    excursion.current = 0
    origin.current = null
  }

  const advance = () => {
    flight.current = null
    deciding.current = false
    setFlipped(false)
    setFlying(null)
    settle()
    setIndex((current) => current + 1)
  }

  // A card in flight when this unmounts — the tab switch to the spread, or the
  // facilitator moving the round on — must not land on a component that is gone.
  useEffect(
    () => () => {
      if (flight.current !== null) window.clearTimeout(flight.current)
    },
    []
  )

  // The deck has landed. Nothing about the round depends on this — it only takes
  // the arrival classes back off, so a card dealt later cannot inherit them.
  useEffect(() => {
    const settled = window.setTimeout(() => setIntro(false), DEAL_IN_TOTAL_MS)
    return () => window.clearTimeout(settled)
  }, [])

  const decide = (direction: "left" | "right", thrownCardId: CardId) => {
    if (done || readOnly || deciding.current) return
    /*
     * Someone who throws inside the first second has seen enough of the arrival.
     * Ending it here matters: the peeks are keyed by card id, so advancing while
     * it runs would mount a fresh layer that flies in from off-screen in the
     * middle of a deal.
     */
    setIntro(false)
    if (direction === "right") {
      /*
       * Throwing right at a card you already hold takes it back OUT of the hand,
       * and stays on it. That is the only way to change your mind without
       * leaving the deal, and advancing afterwards would hide the result of the
       * press you just made.
       */
      if (picked.includes(thrownCardId)) {
        onDrop(thrownCardId)
        settle()
        return
      }
      // A full hand can still skip — it just cannot keep. Silently advancing on
      // a blocked keep would read as the card having been taken.
      if (capReached) {
        settle()
        return
      }
      onKeep(thrownCardId)
    }
    deciding.current = true
    setFlying(direction)
    flight.current = window.setTimeout(advance, FLIGHT_MS)
  }

  const onPointerDown = (event: React.PointerEvent) => {
    if (done || readOnly || deciding.current) return
    /*
     * Never capture a press that started on a control.
     *
     * `setPointerCapture` on the card retargets every later pointer event to the
     * card, including the pointerup — so the browser never completes the click on
     * the button underneath, and the flip button on this card did nothing at all.
     * A press on a button is not the start of a throw.
     */
    if ((event.target as HTMLElement).closest("button")) return
    origin.current = event.clientX
    travel.current = 0
    excursion.current = 0
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (origin.current === null) return
    travel.current = event.clientX - origin.current
    excursion.current = Math.max(excursion.current, Math.abs(travel.current))
    setDrag(travel.current)
  }

  const onPointerUp = () => {
    if (origin.current === null) return
    const travelled = travel.current
    if (done || Math.abs(travelled) < THROW_THRESHOLD_PX) {
      // A tap turns the card over. Anything short of the throw threshold but
      // past the tap one is an abandoned drag, not a request to read the
      // back, so it only springs back.
      if (!done && excursion.current <= TAP_THRESHOLD_PX) setFlipped((value) => !value)
      settle()
      return
    }
    origin.current = null
    decide(travelled > 0 ? "right" : "left", deck[index])
  }

  if (done) {
    return (
      <div className={styles.dealEnd}>
        <h2 className={styles.dealEndTitle}>{CARDS_COPY.dealEndTitle}</h2>
        <p className={styles.dealEndBody}>{CARDS_COPY.dealEndBody}</p>
        <div className={styles.dealEndActions}>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => {
              setIndex(0)
              setFlipped(false)
              settle()
            }}
          >
            {CARDS_COPY.dealAgain}
          </button>
          <button type="button" className={styles.ghostButton} onClick={onSeeSpread}>
            {CARDS_COPY.toSpread}
          </button>
        </div>
      </div>
    )
  }

  const cardId = deck[index]
  const held = picked.includes(cardId)
  /*
   * Where the card is: under the finger, laid down (a keep), or off the left
   * edge (a skip). The placement straightens the drag's tilt rather than
   * carrying it, because a card put down flat is square to the table.
   */
  const offset = flying === "left" ? -FLIGHT_PX : drag
  const transform =
    flying === "right"
      ? `translate(0px, ${PLACE_DOWN_PX}px) scale(${PLACE_SCALE})`
      : `translateX(${offset}px) rotate(${offset * 0.04}deg)`
  const { short } = cardTint(cardId)

  const keepLabel = held ? CARDS_COPY.dropIt : capReached ? CARDS_COPY.keepFull : CARDS_COPY.keep

  const peeks = deck.slice(index + 1, index + 1 + PEEK.length)
  /* Everything under the peeks: during the arrival, a sample across the families. */
  const extras = intro ? sampleFamilies(deck.slice(index + 1 + peeks.length)) : []

  /*
   * Deepest first, one stagger apart. The order is counted from the bottom of
   * the pile so the card the room is meant to read is the last thing to land,
   * and the stagger is the span divided by the gaps between the cards actually
   * in play — see DEAL_IN_SPAN_MS.
   */
  /*
   * Where each layer of the pile sits.
   *
   * While a card is being thrown every layer moves up one, the nearest peek
   * taking the thrown card's own place — so the deck rises to fill the gap as it
   * opens rather than re-indexing once it has. Without it the next card simply
   * appeared, flat and full-strength in the middle of the table, one frame after
   * the last one left: a list that shifted rather than a deck being dealt from.
   *
   * The handoff is what makes it work. `.peek` transitions for exactly
   * FLIGHT_MS, so by the time `advance` replaces the risen peek with the real
   * top card the two occupy the same place at the same shade, and swapping one
   * for the other is invisible.
   */
  const shapeAt = (depth: number) => (flying ? (depth === 0 ? TOP : PEEK[depth - 1]) : PEEK[depth])

  const layers = extras.length + peeks.length + 1
  const stagger = layers > 1 ? DEAL_IN_SPAN_MS / (layers - 1) : 0
  const arrival = (order: number) =>
    intro ? { animationDelay: `${Math.round(order * stagger)}ms` } : undefined

  return (
    <div className={styles.deal}>
      <div className={styles.familyNav}>
        <button
          type="button"
          className={styles.familyNavBtn}
          disabled={familyOrdinal === 0 || flying !== null}
          aria-label={CARDS_COPY.previousFamily}
          onClick={() => goToFamily(-1)}
        >
          ‹
        </button>
        <span className={styles.familyNavLabel}>{short}</span>
        <button
          type="button"
          className={styles.familyNavBtn}
          disabled={familyOrdinal === familyStarts.length - 1 || flying !== null}
          aria-label={CARDS_COPY.nextFamily}
          onClick={() => goToFamily(1)}
        >
          ›
        </button>
      </div>

      <div className={styles.dealRow}>
        <div className={styles.throwSide}>
          <button
            type="button"
            className={styles.throwButton}
            title={CARDS_COPY.skip}
            aria-label={CARDS_COPY.skip}
            disabled={readOnly}
            onClick={() => decide("left", cardId)}
          >
            <SkipIcon />
          </button>
          <span className={styles.throwLabel}>{CARDS_COPY.skip}</span>
        </div>

        {/*
         * Every layer is a wrapper carrying the flight and an inner card
         * carrying its resting tilt. One element cannot do both: the arrival
         * animates `transform`, which is the same property the tilt is set in,
         * so a single element would fly in flat and snap to its angle at the
         * end. Painting order is DOM order throughout — no z-index on the
         * layers — because a transformed wrapper makes its own stacking context
         * while it animates, and anything keyed off z-index would reorder the
         * pile the moment the intro finished.
         */}
        <div className={styles.stack}>
          {extras.map((extraId, order) => (
            <span
              key={extraId}
              className={`${styles.layer} ${styles.dealIn}`}
              style={arrival(order)}
              aria-hidden="true"
            >
              <Peek cardId={extraId} shape={DEEPEST} />
            </span>
          ))}

          {/*
           * The cards peeking from underneath, rendered nearest last so the DOM
           * order matches the stacking, and dimmed with depth so the top card
           * stays the only one being read.
           */}
          {peeks
            .map((peekId, depth) => ({ peekId, shape: shapeAt(depth), depth }))
            .reverse()
            .map(({ peekId, shape, depth }) => (
              <span
                key={peekId}
                className={intro ? `${styles.layer} ${styles.dealIn}` : styles.layer}
                style={arrival(extras.length + peeks.length - 1 - depth)}
                aria-hidden="true"
              >
                <Peek cardId={peekId} shape={shape} />
              </span>
            ))}

          <div
            className={intro ? styles.dealIn : undefined}
            style={arrival(extras.length + peeks.length)}
          >
            <div
              className={
                flying
                  ? `${flying === "right" ? styles.placed : styles.thrown} ${styles.throwable}`
                  : styles.throwable
              }
              style={{ transform, opacity: flying ? 0 : 1 }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <CardFace
                cardId={cardId}
                side={flipped ? "back" : "front"}
                size="lg"
                className={held ? styles.heldCard : undefined}
              />
              {!flying ? (
                <div className={styles.cardFoot}>
                  {readOnly ? null : (
                    <span className={styles.dragHint} aria-hidden="true">
                      {CARDS_COPY.dragHint}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.flipPill}
                    onClick={() => setFlipped((value) => !value)}
                  >
                    {flipped ? CARDS_COPY.flipToFront : CARDS_COPY.flipToBack}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/*
          Three states, not two. A card already in the hand offers to come back
          out — dropping from the deal is the only way to swap a card once the
          hand is full without leaving for the spread.
        */}
        <div className={styles.throwSide}>
          <button
            type="button"
            className={
              held ? styles.throwButton : `${styles.throwButton} ${styles.throwButtonPrimary}`
            }
            title={keepLabel}
            aria-label={keepLabel}
            disabled={readOnly || (capReached && !held)}
            onClick={() => decide("right", cardId)}
          >
            {held ? "−" : <KeepIcon />}
          </button>
          <span className={styles.throwLabel}>{keepLabel}</span>
        </div>
      </div>

      <div className={styles.dealFoot}>
        {index === 0 ? null : (
          <button
            type="button"
            className={styles.linkButton}
            disabled={flying !== null}
            onClick={() => {
              setIndex((current) => Math.max(current - 1, 0))
              setFlipped(false)
              settle()
            }}
          >
            ← {CARDS_COPY.backACard}
          </button>
        )}
        <span className={styles.dealPosition}>{CARDS_COPY.dealPicked(picked.length, cap)}</span>
        {/* Once the hand is full the useful sentence is how to swap, not how to drag. */}
        {readOnly ? null : (
          <span className={styles.throwHint}>
            {capReached && !held ? CARDS_COPY.handFullHint : CARDS_COPY.dragToDecide}
          </span>
        )}
      </div>
    </div>
  )
}
